import { useBlockProps, InspectorControls, BlockControls } from "@wordpress/block-editor";
import { PanelBody, SelectControl, TextControl, Button, ToggleControl, Disabled, ToolbarGroup, ToolbarDropdownMenu } from "@wordpress/components";
import { useSelect } from "@wordpress/data";
import DebouncedServerSideRender from "../../../shared/js/DebouncedServerSideRender";
import { __ } from "@wordpress/i18n";
import collectTocItems, { buildNestedTocItems } from "../../../shared/js/collectTocItems";
import { getDuplicateTocAnchors } from "../../../shared/js/tocAnchorDiagnostics";

const VARIANT_OPTIONS = [
    { label: __("Default", "content-egg"), value: "default" },
    { label: __("Compact", "content-egg"), value: "compact" },
    { label: __("Numbered", "content-egg"), value: "numbered" },
];

const COLOR_SCHEME_OPTIONS = [
    { label: __("Auto (site default)", "content-egg"), value: "auto" },
    { label: __("Light", "content-egg"), value: "light" },
    { label: __("Dark", "content-egg"), value: "dark" },
];

const SOURCE_MODE_OPTIONS = [
    { label: __("Automatic", "content-egg"), value: "auto" },
    { label: __("Manual", "content-egg"), value: "manual" },
];

export default function Edit({ attributes, setAttributes }) {
    const blockProps = useBlockProps({ className: attributes.align ? `align${attributes.align}` : "" });
    const { variant, label, source_mode, items = [], collapsible, default_open, color_scheme } = attributes;
    const allBlocks = useSelect((select) => (
        source_mode !== "auto" ? [] : select("core/block-editor").getBlocks()
    ), [source_mode]);
    const automaticItems = useSelect((select) => {
        if (source_mode !== "auto") {
            return [];
        }

        const blocks = select("core/block-editor").getBlocks();
        return collectTocItems(blocks);
    }, [source_mode]);

    const updateItems = (nextItems) => setAttributes({ items: nextItems });
    const addItem = () => updateItems([...(items || []), { text: "", anchor: "", sub_items: [] }]);
    const updateItem = (index, key, value) => {
        const next = [...(items || [])];
        next[index] = { text: "", anchor: "", sub_items: [], ...(next[index] || {}), [key]: value };
        updateItems(next);
    };
    const removeItem = (index) => {
        const next = [...(items || [])];
        next.splice(index, 1);
        updateItems(next);
    };
    const addSubItem = (index) => {
        const next = [...(items || [])];
        const current = next[index] || { text: "", anchor: "", sub_items: [] };
        next[index] = { ...current, sub_items: [...(current.sub_items || []), { text: "", anchor: "" }] };
        updateItems(next);
    };
    const updateSubItem = (index, subIndex, key, value) => {
        const next = [...(items || [])];
        const current = next[index] || { text: "", anchor: "", sub_items: [] };
        const subItems = [...(current.sub_items || [])];
        subItems[subIndex] = { text: "", anchor: "", ...(subItems[subIndex] || {}), [key]: value };
        next[index] = { ...current, sub_items: subItems };
        updateItems(next);
    };
    const removeSubItem = (index, subIndex) => {
        const next = [...(items || [])];
        const current = next[index] || { text: "", anchor: "", sub_items: [] };
        const subItems = [...(current.sub_items || [])];
        subItems.splice(subIndex, 1);
        next[index] = { ...current, sub_items: subItems };
        updateItems(next);
    };

    const hasManualPreviewContent = (items || []).some((item) => (
        (item?.text || "").trim() !== "" ||
        (item?.anchor || "").trim() !== "" ||
        (item?.sub_items || []).some((subItem) => (subItem?.text || "").trim() !== "" || (subItem?.anchor || "").trim() !== "")
    ));
    const hasPreviewContent = source_mode === "auto" ? automaticItems.length > 0 : hasManualPreviewContent;
    const duplicateAnchors = source_mode === "auto" ? getDuplicateTocAnchors(allBlocks) : [];
    const previewItems = source_mode === "auto" && variant === "default"
        ? buildNestedTocItems(automaticItems)
        : automaticItems;

    return (
        <>
            <BlockControls>
                <ToolbarGroup>
                    <ToolbarDropdownMenu
                        icon="screenoptions"
                        label={__("Variant", "content-egg")}
                        text={VARIANT_OPTIONS.find((o) => o.value === variant)?.label}
                        controls={VARIANT_OPTIONS.map((option) => ({
                            title: option.label,
                            isActive: variant === option.value,
                            onClick: () => setAttributes({ variant: option.value }),
                        }))}
                    />
                </ToolbarGroup>
            </BlockControls>

            <InspectorControls>
                <PanelBody title={__("Layout", "content-egg")} initialOpen={true}>
                    <SelectControl
                        label={__("Variant", "content-egg")}
                        value={variant}
                        options={VARIANT_OPTIONS}
                        onChange={(value) => setAttributes({ variant: value })}
                    />
                    <SelectControl
                        label={__("Color Scheme", "content-egg")}
                        value={color_scheme}
                        options={COLOR_SCHEME_OPTIONS}
                        onChange={(value) => setAttributes({ color_scheme: value })}
                    />
                    <SelectControl
                        label={__("Source Mode", "content-egg")}
                        value={source_mode}
                        options={SOURCE_MODE_OPTIONS}
                        onChange={(value) => setAttributes({ source_mode: value })}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>

                <PanelBody title={__("Content", "content-egg")} initialOpen={true}>
                    <TextControl
                        label={__("Label", "content-egg")}
                        value={label}
                        onChange={(value) => setAttributes({ label: value })}
                    />
                    <ToggleControl
                        label={__("Collapsible", "content-egg")}
                        checked={!!collapsible}
                        onChange={(value) => setAttributes({ collapsible: value })}
                    />
                    <ToggleControl
                        label={__("Open By Default", "content-egg")}
                        checked={!!default_open}
                        onChange={(value) => setAttributes({ default_open: value })}
                        disabled={!collapsible}
                    />
                    <ToggleControl
                        label={__("Show Products", "content-egg")}
                        help={__("Show product-card navigation below the table of contents.", "content-egg")}
                        checked={!!attributes.show_products}
                        onChange={(value) => setAttributes({ show_products: value })}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>

                {source_mode === "manual" && (
                    <PanelBody title={__("Items", "content-egg")} initialOpen={false}>
                    {(items || []).map((item, index) => (
                        <div
                            key={index}
                            style={{
                                marginBottom: index === items.length - 1 ? "0" : "10px",
                                paddingBottom: index === items.length - 1 ? "0" : "10px",
                                borderBottom: index === items.length - 1 ? "none" : "1px solid #e0e0e0",
                            }}
                        >
                            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "6px" }}>
                                <Button
                                    icon="no-alt"
                                    isDestructive
                                    size="small"
                                    onClick={() => removeItem(index)}
                                    label={__("Remove", "content-egg")}
                                />
                            </div>
                            <TextControl
                                label={__("Text", "content-egg")}
                                value={item?.text || ""}
                                onChange={(value) => updateItem(index, "text", value)}
                            />
                            <TextControl
                                label={__("Anchor", "content-egg")}
                                value={item?.anchor || ""}
                                onChange={(value) => updateItem(index, "anchor", value)}
                                help={__("Use the target ID with or without #.", "content-egg")}
                            />
                            {variant === "default" && (
                                <div style={{ marginTop: "8px" }}>
                                    <div style={{ fontSize: "11px", fontWeight: 500, marginBottom: "6px" }}>
                                        {__("Sub-items", "content-egg")}
                                    </div>
                                    {(item?.sub_items || []).map((subItem, subIndex) => (
                                        <div
                                            key={subIndex}
                                            style={{
                                                marginLeft: "12px",
                                                marginBottom: subIndex === (item?.sub_items || []).length - 1 ? "6px" : "8px",
                                                paddingLeft: "10px",
                                                borderLeft: "1px solid #e0e0e0",
                                            }}
                                        >
                                            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "4px" }}>
                                                <Button
                                                    icon="no-alt"
                                                    isDestructive
                                                    size="small"
                                                    onClick={() => removeSubItem(index, subIndex)}
                                                    label={__("Remove", "content-egg")}
                                                />
                                            </div>
                                            <TextControl
                                                label={__("Sub-item Text", "content-egg")}
                                                value={subItem?.text || ""}
                                                onChange={(value) => updateSubItem(index, subIndex, "text", value)}
                                            />
                                            <TextControl
                                                label={__("Sub-item Anchor", "content-egg")}
                                                value={subItem?.anchor || ""}
                                                onChange={(value) => updateSubItem(index, subIndex, "anchor", value)}
                                                __nextHasNoMarginBottom
                                            />
                                        </div>
                                    ))}
                                    <Button variant="secondary" size="small" onClick={() => addSubItem(index)}>
                                        {__("Add sub-item", "content-egg")}
                                    </Button>
                                </div>
                            )}
                        </div>
                    ))}
                    <Button variant="secondary" size="small" onClick={addItem} style={{ marginTop: "10px" }}>
                        {__("Add item", "content-egg")}
                    </Button>
                    </PanelBody>
                )}
            </InspectorControls>

            <div {...blockProps}>
                {hasPreviewContent ? (
                    source_mode === "auto" ? (
                        <Disabled>
                            <AutoTocPreview
                                variant={variant}
                                label={label}
                                collapsible={collapsible}
                                defaultOpen={default_open}
                                items={previewItems}
                                duplicateAnchors={duplicateAnchors}
                                showProducts={!!attributes.show_products}
                            />
                        </Disabled>
                    ) : (
                        <Disabled>
                            <DebouncedServerSideRender
                                block="eggb/toc"
                                attributes={attributes}
                            />
                        </Disabled>
                    )
                ) : (
                    <div
                        style={{
                            minHeight: "120px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "20px",
                            border: "1px dashed #c3c4c7",
                            borderRadius: "6px",
                            background: "#f6f7f7",
                            textAlign: "center",
                        }}
                    >
                        <p style={{ color: "#757575", fontStyle: "italic", margin: 0, fontSize: "13px" }}>
                            {source_mode === "auto"
                                ? __("Add TOC metadata to supported blocks in the post.", "content-egg")
                                : __("Add TOC items in the sidebar.", "content-egg")}
                        </p>
                    </div>
                )}
            </div>
        </>
    );
}

function AutoTocPreview({ variant, label, collapsible, defaultOpen, items, duplicateAnchors, showProducts }) {
    const labelText = (label || "").trim() || __("In this article", "content-egg");
    const renderItemPrefix = (item, index) => {
        if (variant === "numbered") {
            return (
                <span className="eggb-toc-preview-badge">
                    {index + 1}
                </span>
            );
        }

        return null;
    };

    const renderSubItems = (subItems) => {
        if (variant !== "default" || !Array.isArray(subItems) || subItems.length === 0) {
            return null;
        }

        return (
            <div className="eggb-toc-preview-sub-list">
                {subItems.map((subItem) => (
                    <div key={`${subItem.anchor}-${subItem.label}`} className={`eggb-toc-preview-item eggb-toc-preview-item--child eggb-toc-level-${subItem.level}`}>
                        <div className="eggb-toc-preview-link-row">
                            <span className="eggb-toc-preview-link-text">{subItem.label}</span>
                        </div>
                        <div className="eggb-toc-preview-anchor">#{subItem.anchor}</div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <nav className={`eggb-block eggb-card eggb-toc eggb-toc--${variant} eggb-toc-preview`} aria-label={__("Table of contents", "content-egg")}>
            <div className="d-flex align-items-center justify-content-between gap-2">
                <p className="eggb-toc-label mb-0">{labelText}</p>
            </div>

            <div className="eggb-toc-preview-meta">
                {collapsible
                    ? (defaultOpen
                        ? __("Automatic preview from supported blocks, expanded by default", "content-egg")
                        : __("Automatic preview from supported blocks, collapsed by default", "content-egg"))
                    : __("Automatic preview from supported blocks", "content-egg")}
            </div>

            {duplicateAnchors.length > 0 && (
                <div className="eggb-toc-preview-warning" role="alert">
                    <div className="eggb-toc-preview-warning-title">
                        {__("Duplicate anchors detected", "content-egg")}
                    </div>
                    <div className="eggb-toc-preview-warning-list">
                        {duplicateAnchors.map((anchor) => `#${anchor}`).join(", ")}
                    </div>
                </div>
            )}

            <div className={variant === "compact" ? "eggb-toc-preview-list eggb-toc-preview-list--compact" : "eggb-toc-preview-list"}>
                {items.map((item, index) => (
                    <div key={`${item.anchor}-${item.label}`} className={`eggb-toc-preview-item eggb-toc-level-${item.level}`}>
                        <div className="eggb-toc-preview-link-row">
                            {renderItemPrefix(item, index)}
                            <span className="eggb-toc-preview-link-text">{item.label}</span>
                        </div>
                        <div className="eggb-toc-preview-anchor">#{item.anchor}</div>
                        {renderSubItems(item.sub_items)}
                    </div>
                ))}
            </div>

            {showProducts && (
                <div className="eggb-toc-products mt-3" style={{ borderTop: "1px solid var(--eggb-border)", paddingTop: "0.75rem" }}>
                    <p style={{ fontSize: "0.78rem", color: "var(--eggb-text-muted)", fontStyle: "italic", margin: 0 }}>
                        {__("Product card navigation will appear here.", "content-egg")}
                    </p>
                </div>
            )}
        </nav>
    );
}
